// Service to interact with the Go backend for stream management
import config from "../config/environment";

const GO_BACKEND_URL = config.GO_BACKEND_URL;

function getAuthHeaders() {
  const token = sessionStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const streamService = {
  // Get codec information for a stream
  async getStreamCodec(streamId) {
    try {
      const response = await fetch(
        `${GO_BACKEND_URL}/stream/codec/${streamId}`,
        { headers: { ...getAuthHeaders() } }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching codec for stream ${streamId}:`, error);
      throw error;
    }
  },

  // Check if a stream is available
  async isStreamAvailable(streamId) {
    try {
      const response = await fetch(
        `${GO_BACKEND_URL}/stream/codec/${streamId}`,
        { headers: { ...getAuthHeaders() } }
      );
      return response.ok;
    } catch (error) {
      console.error(
        `Error checking stream availability for ${streamId}:`,
        error
      );
      return false;
    }
  },

  // Get all available streams (using the new /streams endpoint)
  async getAvailableStreams() {
    try {
      console.log("Fetching streams from Go backend...");
      const response = await fetch(`${GO_BACKEND_URL}/streams`, {
        headers: { ...getAuthHeaders() },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Received streams data:", data);

      const availableStreams = {};

      // Convert the streams array to our expected format
      if (data.streams && Array.isArray(data.streams)) {
        data.streams.forEach((stream, index) => {
          availableStreams[stream.id] = {
            id: stream.id,
            name: `Item ${index + 1}`, // Generate a friendly name
            url: stream.url,
            onDemand: stream.on_demand,
            disableAudio: stream.disable_audio,
            detectionEnabled: stream.detection_enabled,
            status: stream.status,
          };
          console.log(`Found stream: ${stream.id} - ${stream.url}`);
        });
      }

      console.log("Available streams:", Object.keys(availableStreams));
      return availableStreams;
    } catch (error) {
      console.error("Error fetching streams from Go backend:", error);
      return {};
    }
  },

  // Update an existing stream (detection_enabled, on_demand, disable_audio, url)
  async updateStream(streamId, changes) {
    try {
      const response = await fetch(`${GO_BACKEND_URL}/streams/${streamId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(changes),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error updating stream ${streamId}:`, error);
      throw error;
    }
  },

  // Create a new stream
  async createStream(streamData) {
    try {
      const response = await fetch(`${GO_BACKEND_URL}/streams`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(streamData),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Error creating stream:", error);
      throw error;
    }
  },

  // Scan local network for camera candidates
  async discoverCameras(timeoutMs = 800) {
    try {
      const response = await fetch(`${GO_BACKEND_URL}/cameras/discover?timeout=${timeoutMs}`, {
        headers: { ...getAuthHeaders() },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Error discovering cameras:", error);
      throw error;
    }
  },

  // Delete a stream
  async deleteStream(streamId) {
    try {
      const response = await fetch(`${GO_BACKEND_URL}/streams/${streamId}`, {
        method: "DELETE",
        headers: { ...getAuthHeaders() },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error(`Error deleting stream ${streamId}:`, error);
      throw error;
    }
  },

  // Alternative WebRTC connection method (using /stream endpoint)
  async createWebRTCConnection2(streamUrl, sdp64) {
    try {
      const response = await fetch(`${GO_BACKEND_URL}/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          ...getAuthHeaders(),
        },
        body: new URLSearchParams({
          url: streamUrl,
          sdp64: sdp64,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(
        `Error creating WebRTC connection for URL ${streamUrl}:`,
        error
      );
      throw error;
    }
  },
};

export default streamService;
